import React, { useEffect, useState } from "react";

import { LabService } from "../services/labService";
import { Card } from "./ui/Card";
import type { LabType } from "../types/lab";
import { normalizeLabType } from "../types/lab";

import { type OrganisationDetailsResponse } from "../services/organisationService";

interface CreateLabFormProps {
  onSubmit: (data: SubmissionData) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  isAdmin?: boolean;
  organisation?: OrganisationDetailsResponse;
  availableLabTypes?: LabType[];
  renderInCard?: boolean;
}

interface FormData {
  name: string;
  total_fte: string;
  type_name: LabType | "";
}

interface FormErrors {
  [key: string]: string;
}

interface SubmissionData {
  name: string;
  total_fte: number;
  type_name: LabType;
}

export const CreateLabForm: React.FC<CreateLabFormProps> = ({
  onSubmit,
  onCancel,
  isLoading,
  organisation,
  availableLabTypes,
  renderInCard = true,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    total_fte: "",
    type_name: "",
  });
  const [labTypes, setLabTypes] = useState<LabType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState<boolean>(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string>("");

  const labLimitModalRef = React.useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const fetchLabTypes = async () => {
      if (availableLabTypes && availableLabTypes.length > 0) {
        setLabTypes(availableLabTypes);
        setLoadingTypes(false);
        return;
      }

      try {
        setLoadingTypes(true);
        const data = await LabService.getLabTypes({
          availableForOrg: Boolean(organisation),
        });
        setLabTypes(data.map((option) => normalizeLabType(option, "wet")));
      } catch (error) {
        console.error("Failed to load lab types:", error);
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchLabTypes();
  }, [availableLabTypes, organisation]);

  useEffect(() => {
    if (labTypes.length === 1) {
      const onlyType = labTypes[0];
      if (!onlyType) {
        return;
      }

      setFormData((prev) =>
        prev.type_name === onlyType ? prev : { ...prev, type_name: onlyType },
      );
      return;
    }

    setFormData((prev) => {
      if (!prev.type_name || labTypes.includes(prev.type_name)) {
        return prev;
      }

      return {
        ...prev,
        type_name: "",
      };
    });
  }, [labTypes]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Lab name is required";
    }

    if (!formData.total_fte) {
      newErrors.total_fte = "Total FTE is required";
    } else {
      const fte = parseFloat(formData.total_fte);
      if (isNaN(fte) || fte <= 0) {
        newErrors.total_fte = "FTE must be a positive number";
      }
    }

    if (!formData.type_name) {
      newErrors.type_name = "Lab type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitError("");

    // Check lab limit FIRST
    if (organisation) {
      const currentLabCount = organisation.total_labs ?? 0;
      const labLimit = organisation.lab_limit ?? 0;

      if (labLimit > 0 && currentLabCount >= labLimit) {
        labLimitModalRef.current?.showModal();
        return;
      }
    }

    try {
      await submit();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create lab";
      setSubmitError(message);
    }
  };

  const submit = async (): Promise<void> => {
    const typeName = formData.type_name;
    if (typeName === "") {
      setErrors((prev) => ({
        ...prev,
        type_name: "Lab type is required",
      }));
      return;
    }

    const submissionData: SubmissionData = {
      name: formData.name.trim(),
      total_fte: parseFloat(formData.total_fte),
      type_name: typeName,
    };

    await onSubmit(submissionData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "type_name" ? (value as LabType | "") : value,
    }));
    setSubmitError("");

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const formContent = (
    <>
      {/* Lab Limit Reached Modal */}
      <dialog ref={labLimitModalRef} className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg text-error">Lab Limit Reached</h3>
          <p className="py-4 font-medium">
            You've reached your lab limit for your current subscription.
            Please contact us to increase your plan.
          </p>
          {organisation && (
            <p className="text-sm text-base-content/70">
              Current labs: {organisation.total_labs} / {organisation.lab_limit}
            </p>
          )}
          <div className="modal-action">
            <form method="dialog">
              <button className="btn">Close</button>
            </form>
          </div>
        </div>
      </dialog>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Lab Name */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Lab Name *</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input input-bordered w-full ${errors.name ? "input-error" : ""}`}
                placeholder="Enter lab name"
                disabled={isLoading}
                maxLength={100}
              />
              {errors.name && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.name}
                  </span>
                </label>
              )}
            </div>

            {/* Total FTE */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Total FTE *</span>
              </label>
              <input
                type="number"
                name="total_fte"
                value={formData.total_fte}
                onChange={handleChange}
                className={`input input-bordered w-full ${errors.total_fte ? "input-error" : ""}`}
                placeholder="Enter total FTE capacity"
                disabled={isLoading}
                step="0.1"
              />
              <label className="label">
                {errors.total_fte ? (
                  <span className="label-text-alt text-error">
                    {errors.total_fte}
                  </span>
                ) : (
                  <span className="label-text-alt">
                    The maximum number of full-time equivalent members this lab
                    can accommodate
                  </span>
                )}
              </label>
            </div>

            {/* Lab Type */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Lab Type *</span>
              </label>
              <select
                name="type_name"
                value={formData.type_name}
                onChange={handleChange}
                className={`select select-bordered w-full ${errors.type_name ? "select-error" : ""}`}
                disabled={isLoading || loadingTypes || labTypes.length === 1}
              >
                <option value="">
                  {loadingTypes ? "Loading lab types..." : "Select a lab type"}
                </option>
                {labTypes.map((type) => (
                  <option key={type} value={type}>
                    {type === "dry" ? "Dry Lab" : type === "wet" ? "Wet Lab" : type}
                  </option>
                ))}
              </select>
              {errors.type_name && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.type_name}
                  </span>
                </label>
              )}
            </div>

            {submitError && (
              <div className="alert alert-error">
                <span>{submitError}</span>
              </div>
            )}

            {/* Form Actions */}
            <div className="card-actions pt-4 justify-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading || loadingTypes}
              >
                {isLoading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Creating Lab...
                  </>
                ) : (
                  "Create Lab"
                )}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </form>
    </>
  );

  if (!renderInCard) {
    return formContent;
  }

  return (
    <Card variant="primary">
      <div className={"card-body"}>{formContent}</div>
    </Card>
  );
};
